import * as THREE from 'three/webgpu';
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {SketchContour} from '../meshProxy/SketchContour';
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
import {AbstractSketchEdgeObject} from './AbstractSketchEdgeObject';
import type {SketchCloneContext} from './SketchCloneContext';
import {ParabolaControlPointRole, SketchPointKind} from './ISketchObject';
import type {SketchCollectionObject} from './SketchCollectionObject';
import {SketchPointObject} from './SketchPointObject';
import type {SketchSlotObject} from './SketchSlotObject';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';
import {ensureRaycastAccelerationForObjectTree, refitRaycastAccelerationForGeometry} from '../../raycast-acceleration';
import type {
    SketchParabolaDefinition,
    SketchParabolaFrame,
    SketchParabolaObjectOptions,
} from '../../../types/mesh/sketch/SketchParabolaObject';

export type {
    SketchParabolaDefinition,
    SketchParabolaObjectOptions,
} from '../../../types/mesh/sketch/SketchParabolaObject';

/**
 * 草图抛物线对象。
 * 职责：
 * 1. 使用正式草图边对象表达抛物线，而不是裸 Three.js 线；
 * 2. 维护焦点、顶点、起点和终点 4 个控制点；
 * 3. 在控制点拖拽后重建抛物线定义并同步显示点位。
 */
export class SketchParabolaObject extends AbstractSketchEdgeObject {
    /** 对象运行时类型标记。 */
    public readonly objectType = SketchEntityKind.Parabola;

    /** 草图抛物线运行时标记。 */
    public readonly isSketchParabolaObject = true;

    /** 创建时所属抽象参考面。 */
    public readonly referencePlane: ReferencePlane;

    /** 抛物线定义。 */
    private parabolaDefinition: Required<SketchParabolaDefinition>;

    /** 焦点控制点。 */
    private focalControlPoint: SketchPointObject;

    /** 顶点控制点。 */
    private apexControlPoint: SketchPointObject;

    /** 起点控制点。 */
    private startControlPoint: SketchPointObject;

    /** 终点控制点。 */
    private endControlPoint: SketchPointObject;

    /** 控制点与定义互相同步时的重入保护。 */
    private isSynchronizingControlPoints = false;

    /** 当前抛物线所属的混合闭合轮廓宿主。 */
    private closedCollection: SketchCollectionObject | SketchSlotObject | null = null;

    /** 实例计数，用于生成显示名称。 */
    public static count = 0;

    /**
     * 获取抛物线对象显示名前缀。
     * @returns 显示名前缀。
     */
    public get baseDisplayName(): string {
        return '抛物线';
    }

    /**
     * 构造草图抛物线对象。
     * @param options 构造参数。
     */
    public constructor(options: SketchParabolaObjectOptions) {
        super(options);
        this.referencePlane = options.referencePlane;
        this.parabolaDefinition = {
            ...options.definition,
            focalWorld: options.definition.focalWorld.clone(),
            apexWorld: options.definition.apexWorld.clone(),
            segments: options.definition.segments ?? 64,
        };
        this.parabolaDefinition = this.resolveDefinitionWithMinimumParameterSpan(this.parabolaDefinition);

        this.focalControlPoint = options.focalPoint ?? this.createControlPoint(this.parabolaDefinition.focalWorld);
        this.apexControlPoint = options.apexPoint ?? this.createControlPoint(this.parabolaDefinition.apexWorld);
        this.startControlPoint = options.startPoint ?? this.createControlPoint(
            this.resolveWorldPointByParameter(this.parabolaDefinition.startParameter),
        );
        this.endControlPoint = options.endPoint ?? this.createControlPoint(
            this.resolveWorldPointByParameter(this.parabolaDefinition.endParameter),
        );

        this.registerControlPoint(this.focalControlPoint, ParabolaControlPointRole.Focal);
        this.registerControlPoint(this.apexControlPoint, ParabolaControlPointRole.Apex);
        this.registerControlPoint(this.startControlPoint, ParabolaControlPointRole.Start);
        this.registerControlPoint(this.endControlPoint, ParabolaControlPointRole.End);

        this.name = `${this.baseDisplayName}${SketchParabolaObject.count}`;
        SketchParabolaObject.count += 1;
        this.updateGeometry();
        this.syncControlPointsFromDefinition();
    }

    /**
     * 获取当前抛物线定义快照。
     * @returns 抛物线定义副本。
     */
    public getParabolaDefinition(): Required<SketchParabolaDefinition> {
        return {
            ...this.parabolaDefinition,
            focalWorld: this.parabolaDefinition.focalWorld.clone(),
            apexWorld: this.parabolaDefinition.apexWorld.clone(),
        };
    }

    /**
     * 更新抛物线定义并刷新几何。
     * @param definition 新抛物线定义。
     */
    public updateParabolaDefinition(definition: SketchParabolaDefinition): void {
        const nextDefinition = {
            ...definition,
            focalWorld: definition.focalWorld.clone(),
            apexWorld: definition.apexWorld.clone(),
            segments: definition.segments ?? this.parabolaDefinition.segments,
        };
        this.parabolaDefinition = this.resolveDefinitionWithMinimumParameterSpan(nextDefinition);
        this.updateGeometry();
        this.syncControlPointsFromDefinition();
    }


    /**
     * 通过对象级定义平移抛物线。
     *
     * 设计说明：
     * 1. 抛物线的起点和终点由焦点、顶点与参数区间共同决定，不能按普通点逐个平移；
     * 2. 若先移动焦点或顶点，再单独移动起终点，会触发定义重建并把端点覆盖成中间态结果；
     * 3. 因此这里保持参数区间不变，只整体平移定义中的焦点和顶点，再统一回写全部控制点。
     *
     * @param delta 位移向量。
     * @throws 当抛物线本身或任一控制点不允许拖动时抛出错误。
     */
    public translateParabolaByDefinition(delta: THREE.Vector3): void {
        if (!this.canDrag()) {
            throw new Error('Parabola control point is fixed and translation is blocked.');
        }

        this.parabolaDefinition = {
            ...this.parabolaDefinition,
            focalWorld: this.parabolaDefinition.focalWorld.clone().add(delta),
            apexWorld: this.parabolaDefinition.apexWorld.clone().add(delta),
        };
        this.updateGeometry();
        this.syncControlPointsFromDefinition();
    }


    /**
     * 获取焦点控制点。
     * @returns 焦点控制点。
     */
    public getFocalPoint(): SketchPointObject {
        return this.focalControlPoint;
    }

    /**
     * 获取顶点控制点。
     * @returns 顶点控制点。
     */
    public getApexPoint(): SketchPointObject {
        return this.apexControlPoint;
    }

    /**
     * 获取起点控制点。
     * @returns 起点控制点。
     */
    public getStartPoint(): SketchPointObject {
        return this.startControlPoint;
    }

    /**
     * 获取终点控制点。
     * @returns 终点控制点。
     */
    public getEndPoint(): SketchPointObject {
        return this.endControlPoint;
    }

    /**
     * 替换抛物线起点的拓扑点对象。
     *
     * 该操作只迁移端点引用，抛物线定义和端点坐标由调用方随后统一收口，
     * 从而允许 Corner 在不移动第三条未选实体的前提下建立共享角点。
     *
     * @param point 新起点对象。
     */
    public setStartPoint(point: SketchPointObject): void {
        if (point === this.startControlPoint) {
            return;
        }

        this.unregisterControlPoint(this.startControlPoint, ParabolaControlPointRole.Start);
        this.startControlPoint = point;
        this.registerControlPoint(this.startControlPoint, ParabolaControlPointRole.Start);
    }

    /**
     * 替换抛物线终点的拓扑点对象。
     *
     * @param point 新终点对象。
     */
    public setEndPoint(point: SketchPointObject): void {
        if (point === this.endControlPoint) {
            return;
        }

        this.unregisterControlPoint(this.endControlPoint, ParabolaControlPointRole.End);
        this.endControlPoint = point;
        this.registerControlPoint(this.endControlPoint, ParabolaControlPointRole.End);
    }

    /**
     * 获取全部抛物线控制点。
     * @returns 控制点列表。
     */
    public getControlPoints(): SketchPointObject[] {
        return [
            this.focalControlPoint,
            this.apexControlPoint,
            this.startControlPoint,
            this.endControlPoint,
        ];
    }

    /**
     * 按参考面局部坐标修改抛物线端点。
     * 说明：
     * 1. PropertyManager 的起点/终点 X/Y 不是自由点拖拽，而是“保持端点仍在抛物线上”的参数编辑；
     * 2. 因此这里先按当前定义求出满足目标坐标的曲线上候选点，再选择与当前端点参数最接近的分支；
     * 3. 最终仍通过控制点 `setPosition()` 进入 dirty 与派生刷新链路，避免绕开草图联动。
     * @param role 端点角色。
     * @param axis 参考面局部坐标轴。
     * @param value 目标坐标值。
     * @returns 是否成功命中合法候选点。
     */
    public updateEndpointCoordinateOnReferencePlane(
        role: 'start' | 'end',
        axis: 'x' | 'y',
        value: number,
    ): boolean {
        const currentParameter = role === 'start'
            ? this.parabolaDefinition.startParameter
            : this.parabolaDefinition.endParameter;
        const targetPoint = this.resolveEndpointWorldByCoordinate(currentParameter, axis, value);
        if (!targetPoint) {
            return false;
        }
        const controlPoint = role === 'start' ? this.startControlPoint : this.endControlPoint;
        controlPoint.setPosition(targetPoint);
        return true;
    }

    /**
     * 获取当前抛物线采样曲线点。
     * 说明：
     * 1. 转换实体引用需要稳定的曲线采样，用于 preview 与端点吸附；
     * 2. 这里直接复用抛物线参数定义生成点，不从 BufferGeometry 反读，避免渲染精度影响语义数据。
     * @returns 按当前定义生成的世界坐标曲线点。
     */
    public getCurvePoints(): THREE.Vector3[] {
        return this.resolveCurvePointsByDefinition(this.parabolaDefinition);
    }

    /**
     * 获取线状实体起点协议点。
     * @returns 起点控制点。
     */
    protected getEdgeStartPoint(): SketchPointObject {
        return this.startControlPoint;
    }

    /**
     * 获取线状实体终点协议点。
     * @returns 终点控制点。
     */
    protected getEdgeEndPoint(): SketchPointObject {
        return this.endControlPoint;
    }

    /**
     * 复制当前抛物线对象。
     * @param context 复制上下文。
     * @returns 抛物线副本。
     */
    public cloneSelf(context?: SketchCloneContext): SketchParabolaObject {
        const clonedParabola = new SketchParabolaObject({
            ...this.createCloneBaseOptions(),
            referencePlane: this.referencePlane,
            definition: this.getParabolaDefinition(),
            focalPoint: this.focalControlPoint.cloneSelf(context),
            apexPoint: this.apexControlPoint.cloneSelf(context),
            startPoint: this.startControlPoint.cloneSelf(context),
            endPoint: this.endControlPoint.cloneSelf(context),
        });
        this.registerObjectClone(context, clonedParabola);
        this.cloneSingleTargetConstraintsTo(clonedParabola, context);
        this.syncCommonDisplayStateToClone(clonedParabola);
        return clonedParabola;
    }

    /**
     * 判断当前抛物线是否允许拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public override canDrag(): boolean {
        if (!super.canDrag()) {
            return false;
        }
        return !this.getControlPoints().some((point) => point.isFixed());
    }

    /**
     * 按当前控制点同步抛物线定义与几何。
     * 说明：
     * 1. 拖拽控制点识别采用与自身控制点的对象引用比对，而非点对象上的 `parabolaControlPointRole`
     *    单值字段——共享端点会被后注册的抛物线覆盖该字段，导致先注册的抛物线判定错乱；
     * 2. 命中自身控制点引用时按角色重建定义；未命中（点属于其他实体）时走基类刷新并回贴。
     * @param movedPoint 本次发生位置变化的控制点。
     */
    public override syncEndpointsToGeometry(movedPoint?: SketchPointObject): void {
        if (this.isSynchronizingControlPoints) {
            return;
        }

        if (movedPoint) {
            const role = this.resolveControlPointRoleOf(movedPoint);
            if (role) {
                this.rebuildFromDraggedControlPoint(role, movedPoint);
                return;
            }
        }

        super.syncEndpointsToGeometry(movedPoint);
        this.syncControlPointsFromDefinition();
    }

    /**
     * 按对象引用解析拖动点在本抛物线上的控制点角色。
     * 说明：共享端点场景下点对象上的 `parabolaControlPointRole` 字段只保存最后一次注册的角色，
     * 不能作为拖拽判定依据，因此这里直接与自身 4 个控制点做引用比对。
     * @param point 待判定的草图点对象。
     * @returns 命中时返回对应角色；否则返回 null。
     */
    private resolveControlPointRoleOf(point: SketchPointObject): ParabolaControlPointRole | null {
        if (this.focalControlPoint === point) {
            return ParabolaControlPointRole.Focal;
        }
        if (this.apexControlPoint === point) {
            return ParabolaControlPointRole.Apex;
        }
        if (this.startControlPoint === point) {
            return ParabolaControlPointRole.Start;
        }
        if (this.endControlPoint === point) {
            return ParabolaControlPointRole.End;
        }
        return null;
    }

    /**
     * 仅刷新当前抛物线几何，不触发约束求解。
     * 说明：
     * 1. 统一参数运行时会先静默写入控制点，再调用边对象刷新几何；
     * 2. 抛物线几何依赖焦点、顶点和起终参数定义，不能直接沿用旧定义刷新；
     * 3. 因此这里先从 4 个控制点反推定义，再重建曲线并把端点吸附回精确抛物线。
     */
    public override refreshGeometryOnly(): void {
        this.rebuildDefinitionFromControlPoints();
        this.updateGeometry();
        this.syncControlPointsFromDefinition();
    }

    /**
     * 绑定所属封闭线段几何对象。
     * 说明：
     * 1. 单条抛物线本身不是闭合 profile；
     * 2. 当它作为混合闭合轮廓的一条边时，轮廓生命周期由集合或槽口宿主统一管理；
     * 3. 这里仅保存拓扑宿主并复用宿主轮廓，保证退出草图后仍可从边恢复 profile。
     * @param geometry 目标封闭线段几何对象或槽口对象。
     * @throws 当当前抛物线已绑定其他封闭几何对象时抛出错误。
     */
    public attachClosedCollection(geometry: SketchCollectionObject | SketchSlotObject): void {
        if (this.closedCollection && this.closedCollection !== geometry) {
            throw new Error('当前抛物线已关联其他封闭线段几何对象。');
        }

        this.closedCollection = geometry;
        if (isSketchSlotObject(geometry)) {
            this.contour = geometry.contour;
            return;
        }

        this.contour = geometry.getContour();
    }

    /**
     * 解绑所属封闭线段几何对象。
     * @param geometry 若传入，仅在匹配时才解绑。
     */
    public detachClosedCollection(geometry?: SketchCollectionObject | SketchSlotObject): void {
        if (geometry && this.closedCollection !== geometry) {
            return;
        }

        this.closedCollection = null;
        this.clearContour();
    }

    /**
     * 判断抛物线是否可创建轮廓。
     * 说明：只有当抛物线已被闭合宿主接管时，才允许委托宿主创建轮廓。
     * @returns 若已关联封闭线段几何对象则返回 true。
     */
    public canCreateContour(): boolean {
        return this.closedCollection !== null;
    }

    /**
     * 创建抛物线所属轮廓。
     * 说明：实际轮廓由封闭线段几何对象统一生成，当前方法只做委托。
     * @returns 创建后的轮廓对象。
     */
    public createContour(): SketchContour {
        return this.createContourFromHost(this.closedCollection, '抛物线');
    }

    /**
     * 基于当前抛物线定义重建几何。
     */
    protected updateGeometry(): void {
        const points = this.resolveCurvePointsByDefinition(this.parabolaDefinition);
        const shouldRebuildRaycastAcceleration = this.syncSampledLineGeometry(points);
        this.geometry.computeBoundingBox();
        this.geometry.computeBoundingSphere();
        if (shouldRebuildRaycastAcceleration) {
            // 采样段数变化后，新几何必须重新建立与新顶点拓扑匹配的射线加速结构。
            ensureRaycastAccelerationForObjectTree(this);
        } else {
            // 控制点拖动保持 65 点拓扑，只需原位校正既有 BVH。
            refitRaycastAccelerationForGeometry(this.geometry);
        }

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }
    }

    /**
     * 创建一个抛物线控制点。
     * @param position 控制点世界坐标。
     * @returns 控制点对象。
     */
    private createControlPoint(position: THREE.Vector3): SketchPointObject {
        return new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,
        });
    }

    /**
     * 注册控制点及其角色。
     * 说明：`parabolaControlPointRole` 是点对象上的展示性单值标记（供求解快照、克隆与参考关系等模块读取），
     * 共享端点场景下会被后注册的抛物线覆盖，因此拖拽判定不得依赖该字段（见 `syncEndpointsToGeometry` 的引用比对）。
     * @param point 控制点对象。
     * @param role 控制点角色。
     */
    private registerControlPoint(point: SketchPointObject, role: ParabolaControlPointRole): void {
        point.parabolaControlPointRole = role;
        if (role === ParabolaControlPointRole.Start || role === ParabolaControlPointRole.End) {
            point.attachEndpointRole();
        }
        if (role === ParabolaControlPointRole.Focal) {
            point.isCenterPoint = true;
        }
        point.registerConnectedEdge(this);
        this.registerRelatedSketchPoint(point);
    }

    /**
     * 解除单个抛物线控制点的对象关系。
     *
     * @param point 待解除控制点。
     * @param role 控制点角色。
     */
    private unregisterControlPoint(point: SketchPointObject, role: ParabolaControlPointRole): void {
        point.unregisterConnectedEdge(this);
        this.unregisterRelatedSketchPoint(point);
        if (role === ParabolaControlPointRole.Start || role === ParabolaControlPointRole.End) {
            point.detachEndpointRole();
        }
        if (point.parabolaControlPointRole === role) {
            point.parabolaControlPointRole = null;
        }
        if (role === ParabolaControlPointRole.Focal) {
            point.isCenterPoint = false;
        }
    }

    /**
     * 根据拖拽控制点重建抛物线定义。
     * @param role 被拖拽控制点角色。
     * @param draggedPoint 被拖拽控制点。
     */
    private rebuildFromDraggedControlPoint(
        role: ParabolaControlPointRole,
        draggedPoint: SketchPointObject,
    ): void {
        this.isSynchronizingControlPoints = true;
        try {
            if (role === ParabolaControlPointRole.Start || role === ParabolaControlPointRole.End) {
                // 共享端点：该点同时被其他边注册连接时，滑动回贴会使各实体对同一拖动位置的投影分叉，
                // 共享点最终只与最后处理的实体共点，先处理实体会视觉断开；
                // 因此改用"变形穿过"模式，保持顶点与轴方向不变、仅沿轴伸缩焦点，使曲线穿过共享点当前位置。
                if (draggedPoint.getConnectedEdgesSnapshot().length > 1) {
                    this.rebuildEndpointThroughSharedPoint(role, draggedPoint.getPosition());
                } else {
                    this.rebuildEndpointBySlide(role, draggedPoint.getPosition());
                }
            } else if (role === ParabolaControlPointRole.Focal) {
                this.parabolaDefinition = {
                    ...this.parabolaDefinition,
                    focalWorld: draggedPoint.getPosition(),
                };
            } else if (role === ParabolaControlPointRole.Apex) {
                this.parabolaDefinition = {
                    ...this.parabolaDefinition,
                    apexWorld: draggedPoint.getPosition(),
                };
            }

            this.updateGeometry();
            this.syncControlPointsFromDefinition();
            this.markOwnerSketchGroupDirty('parabola-control-point-dragged');
        } finally {
            this.isSynchronizingControlPoints = false;
        }
    }

    /**
     * 端点沿抛物线滑动重建定义（非共享端点拖拽的默认语义）。
     * 说明：仅按拖动位置重算端点参数，焦点与顶点不动，端点最终回贴到曲线上。
     * @param role 被拖拽端点角色。
     * @param targetWorld 拖动目标世界坐标。
     */
    private rebuildEndpointBySlide(
        role: ParabolaControlPointRole.Start | ParabolaControlPointRole.End,
        targetWorld: THREE.Vector3,
    ): void {
        const parameter = this.resolveParameterFromWorldPoint(targetWorld);
        const nextDefinition = {
            ...this.parabolaDefinition,
            startParameter: role === ParabolaControlPointRole.Start
                ? parameter
                : this.parabolaDefinition.startParameter,
            endParameter: role === ParabolaControlPointRole.End
                ? parameter
                : this.parabolaDefinition.endParameter,
        };
        this.parabolaDefinition = this.resolveDefinitionWithMinimumParameterSpan(nextDefinition, role);
    }

    /**
     * 共享端点拖拽时按"变形穿过"模式重建抛物线定义。
     * 说明：
     * 1. 共享端点同时被多条边注册连接，若沿用端点沿曲线滑动语义，各实体对同一拖动位置的投影回贴必然分叉，
     *    共享点最终只与最后处理的实体共点，先处理实体会视觉断开；
     * 2. 因此保持顶点与轴方向不变、仅沿轴伸缩焦点：抛物线方程 y = x²/(4d) 过目标点 (x, y) 时反解焦距 d = x²/(4y)，
     *    端点参数取目标点的侧向分量 x，回贴后曲线端点恰好等于共享点当前位置，位移为零；
     * 3. 目标点越过顶点平面或反解焦距退化（焦点与顶点重合）时无有效解，退回滑动语义兜底；
     * 4. 变形模式优先保证共点，不经过最小跨度保护——跨度退化仅发生在用户把两端点拖到重合的极端场景。
     * @param role 被拖拽端点角色。
     * @param targetWorld 共享点当前世界坐标（即拖动目标位置）。
     */
    private rebuildEndpointThroughSharedPoint(
        role: ParabolaControlPointRole.Start | ParabolaControlPointRole.End,
        targetWorld: THREE.Vector3,
    ): void {
        const frame = this.resolveFrame();
        const relative = targetWorld.clone().sub(frame.apexWorld);
        const x = relative.dot(frame.sideDir);
        const y = relative.dot(frame.axisDir);
        const tolerance = SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength;
        // 沿轴分量为非正时，抛物线无法在轴方向不变的前提下穿过目标点，退回滑动语义保证可用性。
        if (y <= tolerance) {
            this.rebuildEndpointBySlide(role, targetWorld);
            return;
        }
        // 按曲线方程反解焦距；焦距退化（目标点位于顶点附近）时同样退回滑动语义。
        const focalLength = (x * x) / (4 * y);
        if (!Number.isFinite(focalLength) || focalLength <= tolerance) {
            this.rebuildEndpointBySlide(role, targetWorld);
            return;
        }
        const nextFocalWorld = frame.apexWorld.clone()
            .add(frame.axisDir.clone().multiplyScalar(focalLength));
        this.parabolaDefinition = {
            ...this.parabolaDefinition,
            focalWorld: nextFocalWorld,
            startParameter: role === ParabolaControlPointRole.Start ? x : this.parabolaDefinition.startParameter,
            endParameter: role === ParabolaControlPointRole.End ? x : this.parabolaDefinition.endParameter,
        };
    }

    /**
     * 根据当前 4 个控制点反推抛物线定义。
     * 说明：
     * 1. 用于统一参数运行时静默写入后的几何收口；
     * 2. 焦点和顶点直接取控制点位置；
     * 3. 起终点只读取其在新抛物线侧向轴上的参数，随后统一回贴到曲线上。
     */
    private rebuildDefinitionFromControlPoints(): void {
        const focalWorld = this.focalControlPoint.getPosition();
        const apexWorld = this.apexControlPoint.getPosition();
        const nextDefinition = {
            ...this.parabolaDefinition,
            focalWorld,
            apexWorld,
        };
        const frame = this.resolveFrameFromDefinition(nextDefinition);
        if (!frame) {
            return;
        }

        this.parabolaDefinition = {
            ...nextDefinition,
            startParameter: this.resolveParameterFromWorldPointByFrame(this.startControlPoint.getPosition(), frame),
            endParameter: this.resolveParameterFromWorldPointByFrame(this.endControlPoint.getPosition(), frame),
        };
        this.parabolaDefinition = this.resolveDefinitionWithMinimumParameterSpan(this.parabolaDefinition);
    }

    /**
     * 解析具备最小跨度的抛物线定义。
     * 说明：
     * 1. 抛物线端点允许反向越过，但可见段不能在越过瞬间退化成零长度；
     * 2. 当拖拽端点进入另一个端点附近的极小范围时，沿拖拽前的端点相对方向保留最小跨度；
     * 3. 这样既不改变 start/end 角色，也避免零长度 BufferGeometry 在渲染中闪烁。
     * @param definition 候选抛物线定义。
     * @param movingEndpointRole 当前正在拖拽的端点角色；非端点重建时传 null。
     * @returns 已消除零跨度退化的定义。
     */
    private resolveDefinitionWithMinimumParameterSpan(
        definition: Required<SketchParabolaDefinition>,
        movingEndpointRole: ParabolaControlPointRole.Start | ParabolaControlPointRole.End | null = null,
    ): Required<SketchParabolaDefinition> {
        const span = definition.endParameter - definition.startParameter;
        if (Math.abs(span) >= SKETCH_GEOMETRY_TOLERANCE_CONFIG.parabolaMinimumParameterSpan) {
            return definition;
        }

        if (movingEndpointRole === ParabolaControlPointRole.Start) {
            const previousDirection = this.resolveParameterDirection(
                this.parabolaDefinition.startParameter - this.parabolaDefinition.endParameter,
                -1,
            );
            return {
                ...definition,
                startParameter: definition.endParameter
                    + previousDirection * SKETCH_GEOMETRY_TOLERANCE_CONFIG.parabolaMinimumParameterSpan,
            };
        }

        if (movingEndpointRole === ParabolaControlPointRole.End) {
            const previousDirection = this.resolveParameterDirection(
                this.parabolaDefinition.endParameter - this.parabolaDefinition.startParameter,
                1,
            );
            return {
                ...definition,
                endParameter: definition.startParameter
                    + previousDirection * SKETCH_GEOMETRY_TOLERANCE_CONFIG.parabolaMinimumParameterSpan,
            };
        }

        const previousSpan = this.parabolaDefinition.endParameter - this.parabolaDefinition.startParameter;
        const direction = this.resolveParameterDirection(previousSpan, 1);
        return {
            ...definition,
            endParameter: definition.startParameter
                + direction * SKETCH_GEOMETRY_TOLERANCE_CONFIG.parabolaMinimumParameterSpan,
        };
    }

    /**
     * 解析非零参数方向。
     * @param delta 用于判定方向的参数差。
     * @param fallbackDirection 参数差为零时使用的兜底方向。
     * @returns 方向值，正数表示参数增大方向，负数表示参数减小方向。
     */
    private resolveParameterDirection(delta: number, fallbackDirection: 1 | -1): 1 | -1 {
        if (delta > 0) {
            return 1;
        }
        if (delta < 0) {
            return -1;
        }
        return fallbackDirection;
    }

    /**
     * 将全部控制点同步到当前定义对应的位置。
     */
    private syncControlPointsFromDefinition(): void {
        this.syncControlPointPosition(this.focalControlPoint, this.parabolaDefinition.focalWorld.clone());
        this.syncControlPointPosition(this.apexControlPoint, this.parabolaDefinition.apexWorld.clone());
        this.syncControlPointPosition(
            this.startControlPoint,
            this.resolveWorldPointByParameter(this.parabolaDefinition.startParameter),
        );
        this.syncControlPointPosition(
            this.endControlPoint,
            this.resolveWorldPointByParameter(this.parabolaDefinition.endParameter),
        );
    }

    /**
     * 判断抛物线控制点是否被其他草图实体共享。
     * 说明：
     * 1. 抛物线构造时会把自身注册为每个控制点的关联边；
     * 2. 当关联边数量大于 1 时，说明绘制链路上有其他实体（如直线）复用了该控制点作为端点；
     * 3. 被共享控制点的位置变更必须走公开联动链路，否则相邻实体几何不会刷新，连接处会断开。
     * @param point 待判定的控制点。
     * @returns 被其他实体共享时返回 true。
     */
    private isControlPointShared(point: SketchPointObject): boolean {
        return point.getConnectedEdgesSnapshot().length > 1;
    }

    /**
     * 同步单个控制点到目标位置。
     * 说明：
     * 1. 未被共享的内部控制点（焦点、顶点、独立端点）走静默写入，避免形成递归刷新闭环；
     * 2. 被其他实体共享的端点使用点对象公开落点链路，通知全部关联边刷新派生几何；
     * 3. 同步期间临时开启本对象重入保护，避免共享点通知当前抛物线再次重建定义；
     * 4. `setPosition` 只负责落点、dirty 和派生刷新，不会启动已删除的对象式约束求解。
     * @param point 目标控制点。
     * @param position 目标世界坐标。
     */
    private syncControlPointPosition(point: SketchPointObject, position: THREE.Vector3): void {
        if (this.isControlPointShared(point)) {
            const wasSynchronizing = this.isSynchronizingControlPoints;
            this.isSynchronizingControlPoints = true;
            try {
                point.setPosition(position);
            } finally {
                this.isSynchronizingControlPoints = wasSynchronizing;
            }
            return;
        }
        point.syncPositionSilently(position);
    }

    /**
     * 根据当前定义解析抛物线局部框架。
     * @returns 抛物线局部框架。
     */
    private resolveFrame(): SketchParabolaFrame {
        const frame = this.resolveFrameFromDefinition(this.parabolaDefinition);
        if (!frame) {
            throw new Error('抛物线定义无效：焦点与顶点不能重合。');
        }
        return frame;
    }

    /**
     * 根据指定定义解析抛物线局部框架。
     * @param definition 抛物线定义。
     * @returns 合法时返回局部框架；焦点与顶点重合时返回 null。
     */
    private resolveFrameFromDefinition(
        definition: Required<SketchParabolaDefinition>,
    ): SketchParabolaFrame | null {
        const normal = this.referencePlane.normal.clone().normalize();
        const axisVector = definition.focalWorld.clone().sub(definition.apexWorld);
        axisVector.sub(normal.clone().multiplyScalar(axisVector.dot(normal)));
        const focalLength = axisVector.length();

        if (focalLength < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }

        const axisDir = axisVector.normalize();
        const sideDir = new THREE.Vector3().crossVectors(axisDir, normal).normalize();
        return {
            apexWorld: definition.apexWorld.clone(),
            focalWorld: definition.focalWorld.clone(),
            axisDir,
            sideDir,
            focalLength,
        };
    }

    /**
     * 按抛物线参数解析世界坐标。
     * @param parameter 抛物线侧向参数。
     * @param frame 可复用的抛物线局部框架。
     * @returns 世界坐标。
     */
    private resolveWorldPointByParameter(
        parameter: number,
        frame: SketchParabolaFrame = this.resolveFrame(),
    ): THREE.Vector3 {
        const axisOffset = (parameter * parameter) / (4 * frame.focalLength);
        return frame.apexWorld.clone()
            .add(frame.sideDir.clone().multiplyScalar(parameter))
            .add(frame.axisDir.clone().multiplyScalar(axisOffset));
    }

    /**
     * 将世界坐标投影为抛物线参数。
     * @param pointWorld 世界坐标。
     * @returns 抛物线参数。
     */
    private resolveParameterFromWorldPoint(pointWorld: THREE.Vector3): number {
        const frame = this.resolveFrame();
        return this.resolveParameterFromWorldPointByFrame(pointWorld, frame);
    }

    /**
     * 在指定抛物线框架中解析世界点对应的侧向参数。
     * @param pointWorld 世界坐标。
     * @param frame 抛物线局部框架。
     * @returns 抛物线侧向参数。
     */
    private resolveParameterFromWorldPointByFrame(
        pointWorld: THREE.Vector3,
        frame: SketchParabolaFrame,
    ): number {
        return pointWorld.clone().sub(frame.apexWorld).dot(frame.sideDir);
    }

    /**
     * 按参考面局部坐标轴值反解曲线上端点世界坐标。
     * @param currentParameter 当前端点参数，用于锁定连续分支。
     * @param axis 参考面局部坐标轴。
     * @param value 目标坐标值。
     * @returns 命中的曲线点；无解时返回 null。
     */
    private resolveEndpointWorldByCoordinate(
        currentParameter: number,
        axis: 'x' | 'y',
        value: number,
    ): THREE.Vector3 | null {
        const frame = this.resolveFrame();
        const apexLocal = this.referencePlane.worldToLocal(frame.apexWorld);
        const axisLocal = this.referencePlane.worldToLocal(
            frame.apexWorld.clone().add(frame.axisDir),
        ).sub(apexLocal.clone());
        const sideLocal = this.referencePlane.worldToLocal(
            frame.apexWorld.clone().add(frame.sideDir),
        ).sub(apexLocal.clone());

        const axisComponent = axis === 'x' ? axisLocal.x : axisLocal.y;
        const sideComponent = axis === 'x' ? sideLocal.x : sideLocal.y;
        const apexComponent = axis === 'x' ? apexLocal.x : apexLocal.y;

        const quadraticA = axisComponent / (4 * frame.focalLength);
        const quadraticB = sideComponent;
        const quadraticC = apexComponent - value;
        const candidateParameters = this.solveCoordinateEquation(quadraticA, quadraticB, quadraticC);
        if (candidateParameters.length === 0) {
            return null;
        }

        const preferredParameter = this.selectPreferredParameterCandidate(candidateParameters, currentParameter);
        return this.resolveWorldPointByParameter(preferredParameter, frame);
    }

    /**
     * 求解单个局部坐标轴对应的抛物线参数方程。
     * @param quadraticA 二次项系数。
     * @param quadraticB 一次项系数。
     * @param quadraticC 常数项。
     * @returns 合法参数候选列表。
     */
    private solveCoordinateEquation(
        quadraticA: number,
        quadraticB: number,
        quadraticC: number,
    ): number[] {
        const epsilon = SKETCH_GEOMETRY_TOLERANCE_CONFIG.trigonometricDenominator;
        if (Math.abs(quadraticA) <= epsilon) {
            if (Math.abs(quadraticB) <= epsilon) {
                return [];
            }
            return [-quadraticC / quadraticB];
        }

        const discriminant = (quadraticB * quadraticB) - (4 * quadraticA * quadraticC);
        if (discriminant < -epsilon) {
            return [];
        }
        const safeDiscriminant = discriminant < 0 ? 0 : discriminant;
        const sqrtDiscriminant = Math.sqrt(safeDiscriminant);
        const denominator = 2 * quadraticA;
        if (Math.abs(sqrtDiscriminant) <= epsilon) {
            return [(-quadraticB) / denominator];
        }
        return [
            ((-quadraticB) + sqrtDiscriminant) / denominator,
            ((-quadraticB) - sqrtDiscriminant) / denominator,
        ];
    }

    /**
     * 从多个参数候选中选择最接近当前端点分支的解。
     * @param candidates 候选参数列表。
     * @param currentParameter 当前端点参数。
     * @returns 优先保持连续性的目标参数。
     */
    private selectPreferredParameterCandidate(candidates: number[], currentParameter: number): number {
        const currentSign = Math.sign(currentParameter);
        return candidates
            .slice()
            .sort((left, right) => {
                const leftDistance = Math.abs(left - currentParameter);
                const rightDistance = Math.abs(right - currentParameter);
                if (
                    Math.abs(leftDistance - rightDistance)
                    > SKETCH_GEOMETRY_TOLERANCE_CONFIG.parabolaDistanceSymmetry
                ) {
                    return leftDistance - rightDistance;
                }
                const leftSignMatch = Math.sign(left) === currentSign ? 0 : 1;
                const rightSignMatch = Math.sign(right) === currentSign ? 0 : 1;
                if (leftSignMatch !== rightSignMatch) {
                    return leftSignMatch - rightSignMatch;
                }
                return Math.abs(left) - Math.abs(right);
            })[0]!;
    }

    /**
     * 按指定抛物线定义生成采样曲线点。
     * @param definition 抛物线定义。
     * @returns 采样曲线点。
     */
    private resolveCurvePointsByDefinition(
        definition: Required<SketchParabolaDefinition>,
    ): THREE.Vector3[] {
        const frame = this.resolveFrameFromDefinition(definition);
        if (!frame) {
            throw new Error('抛物线定义无效：焦点与顶点不能重合。');
        }
        const fromParameter = definition.startParameter;
        const toParameter = definition.endParameter;
        const segments = Math.max(definition.segments, 1);
        const step = (toParameter - fromParameter) / segments;
        const points: THREE.Vector3[] = [];

        for (let index = 0; index <= segments; index += 1) {
            const parameter = fromParameter + step * index;
            points.push(this.resolveWorldPointByParameter(parameter, frame));
        }

        return points;
    }
}
